import {
  Alert,
  Box,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment";
import tripService from "services/tripService";
import { TripModification } from "trips/models";

// Read-only field-by-field change history of the trip: a port of Logistrics' Trip Modifications
// page. Same columns, same rows, same order (the server returns them in Logistrics' order), full
// text, no paging or sorting.
const TripModifications = ({ trip }) => {
  const [rows, setRows] = useState<TripModification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trip?.id) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setRows([]);
    tripService
      .getModifications(trip.id)
      .then((results) => {
        if (!cancelled) setRows(results || []);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Failed to load trip modifications", e);
        setError(e?.message || "Failed to load trip modifications.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trip?.id]);

  const cellSx = { whiteSpace: "pre-wrap", wordBreak: "break-word", verticalAlign: "top", fontSize: ".85rem" };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="body2" sx={{ ml: 1 }}>
          Modifications
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ mt: 2, overflow: "hidden" }}>
          {error ? (
            <Alert severity="error" sx={{ width: "99%" }}>
              Could not load modifications: {error}
            </Alert>
          ) : isLoading ? (
            <Box>
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" width="96%" sx={{ my: 1, ml: 2 }} />
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: "99%" }}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 160 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Old Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>New Value</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 200 }}>UserID</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 130 }}>DateTime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No modifications recorded
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((m, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={cellSx}>{m.field}</TableCell>
                        <TableCell sx={cellSx}>{m.oldValue}</TableCell>
                        <TableCell sx={cellSx}>{m.newValue}</TableCell>
                        <TableCell sx={cellSx}>{m.changedBy}</TableCell>
                        {/* Already in the trip's time zone from the server; printed as-is, not converted. */}
                        <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                          {m.changedAt ? moment.parseZone(m.changedAt).format("MM/DD/YYYY HH:mm") : ""}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Grid>
    </>
  );
};

export default TripModifications;
